import { inject, Injectable } from '@angular/core';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import { IntellectualRecordsService } from '@common/services/intellectual-records/intellectual-records.service';
import {
    ActionDisabledCondition,
    ActionItemOptions,
    MhSelection,
    PersonalSelectionService,
    UserAction
} from '@mediahaven/shared';
import { ApiFragment, DialogStyle, Mh2Entity, NavigationLink, RecordType } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionService } from '../../abstract/action.service.interface';

@Injectable({ providedIn: 'root' })
export class AddToSelectionActionService extends ActionService {
    protected readonly action: MediaHavenActionType = MediaHavenActionType.ADD_TO_SELECTION;

    // Services
    private readonly selectionService: PersonalSelectionService = inject(PersonalSelectionService);
    private readonly intellectualRecordsService: IntellectualRecordsService = inject(IntellectualRecordsService);
    private readonly dialog: DialogHelper = inject(DialogHelper);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Add To Selection
     * Available when:
     *  * User has a selection item
     *  * Item is published or archived (== is not concept)
     *  * Item is not in selection
     *  * Item is not deleted
     *  * Item is not a representation
     *
     * Disabled when:
     *  * Never
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return this.selectionService.getMhSelection().pipe(
            catchError(error => {
                return of(null);
            }),
            map(selection => {
                return this.userActionBuilder(record, selection);
            })
        );
    }

    public userActionBuilder(record: ApiFragment, selection: MhSelection | null): UserAction {
        const isConcept: boolean = record.isConcept();

        const hasSelection: boolean = selection !== null;
        const isInSelection: boolean = selection ? record.isInSelection(selection.fragmentId) : false;
        const isRepresentation: boolean = record.getRecordType() === RecordType.Representation;
        const isDeleted: boolean = record.isDeleted();

        const availableConditions: boolean[] = [
            !isConcept,
            hasSelection,
            !isInSelection,
            !isDeleted,
            !isRepresentation
        ];
        const disableConditions: ActionDisabledCondition[] = [this.getAddToSelectionDisableCondition(record)];

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(record: ApiFragment, actionItemOption: ActionItemOptions): Observable<boolean> {
        if (record.isNewCollection()) {
            return this.addCollectionToSelection(record, actionItemOption);
        } else {
            return this.addFragmentsToSelection(record, [record], actionItemOption);
        }
    }

    private addCollectionToSelection(
        collection: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Observable<boolean> {
        const title: string = this.translate('collections.add-to-selection-dialog.title');
        const message: string = this.translate('collections.add-to-selection-dialog.message');
        const yes: string = this.translate('collections.add-to-selection-dialog.confirm');
        const no: string = this.translate('actions.common.cancel');

        return this.dialog.confirm({ title, message, yes, no }, DialogStyle.Wide).pipe(
            switchMap(() => this.intellectualRecordsService.getAllPublishedChildren(collection.getRecordId())),
            switchMap(recordsToAdd => this.addFragmentsToSelection(collection, recordsToAdd, actionItemOptions))
        );
    }

    private addFragmentsToSelection(
        record: ApiFragment,
        recordsToAdd: ApiFragment[],
        actionItemOptions: ActionItemOptions
    ): Observable<boolean> {
        this.initBusyToast(record, actionItemOptions, true);

        return this.selectionService.addFragmentsToSelection(recordsToAdd).pipe(map(() => true));
    }

    private getAddToSelectionDisableCondition(record: ApiFragment): ActionDisabledCondition {
        if (record.isNewCollection()) {
            return { condition: false, tooltip: '' };
        }
        if (record.isIntellectualObject()) {
            return { condition: true, tooltip: 'general.add-to-selection.type-error' };
        }

        return { condition: false, tooltip: '' };
    }

    public getLabelKey(target: ApiFragment): string {
        return target.isNewCollection() ? 'actions.selection.add-collection-items' : 'actions.selection.add';
    }

    public getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.add-to-selection';
    }

    public getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public getSuccessToastLink(): NavigationLink | undefined {
        return {
            route: `/selection/overview`
        };
    }

    public getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return 'toasts.see.selection';
    }

    public getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }
}
